import * as React from 'react';
import styled from 'styled-components';
import Theme, { CloseIconTheme } from './theme';
import type { ChipTheme } from './theme';
import type { DataProvidesProps } from '../../utils/constants';
import { getDataProvidesProps } from '../../utils/constants';
import CloseIcon from '../../icons/close';

type ChipProps = {
  text: string,
  onRemove?: Function,
  title?: string,
  ...DataProvidesProps,
};

type ThemeableChipProps = ChipProps & ChipTheme;

const ThemeableChip = React.forwardRef((props: ThemeableChipProps, ref) => {
  const { theme } = props;
  const ChipTextComponent = theme.typography;

  return (
    <ChipWrapper ref={ref} theme={theme} {...getDataProvidesProps(props)}>
      <Ellipsified title={props.text}>
        <ChipTextComponent>{props.text}</ChipTextComponent>
      </Ellipsified>
      {props.onRemove && (
        <CloseIconWrapper onClick={props.onRemove} data-provides="remove-chip">
          <CloseIcon size={'xs'} />
        </CloseIconWrapper>
      )}
    </ChipWrapper>
  );
});
ThemeableChip.displayName = 'ThemeableChip';

const CloseIconWrapper = styled.span`
  display: inline-flex;

  svg {
    top: -2px;
    position: relative;
    margin-left: 4px;
    margin-top: -2px;
    background: ${CloseIconTheme.default.backgroundColor};

    path {
      fill: ${CloseIconTheme.default.color};
    }

    &:hover {
      border-radius: 50%;
      cursor: pointer;
      background: ${CloseIconTheme.hover.backgroundColor};
      path {
        fill: ${CloseIconTheme.hover.color};
      }
    }
  }
`;

const Ellipsified = styled.div`
  max-width: 150px;
  display: inline-block;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const ChipWrapper = styled.span.attrs((props: Props) => ({
  'data-provides': props['data-provides'] || 'chip-wrapper',
  title: props.title || props.text,
}))`
  display: inline-block;
  background: ${({ theme }) => theme.backgroundColor};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 5px;
  box-sizing: border-box;
  line-height: 1rem;
  padding: 3px 6px 0 8px;
  white-space: nowrap;
  &:hover {
    border: 1px solid ${({ theme }) => theme.hoverBorderColor};
  }
`;

const Chip = React.forwardRef((props: ChipProps, ref) => (
  <ThemeableChip {...props} theme={Theme.default} ref={ref} />
));
Chip.displayName = 'Chip';
Chip.defaultProps = { 'data-provides': 'default-chip' };

const ErrorChip = React.forwardRef((props: ChipProps, ref) => (
  <ThemeableChip {...props} theme={Theme.error} ref={ref} />
));
ErrorChip.displayName = 'ErrorChip';
ErrorChip.defaultProps = { 'data-provides': 'error-chip' };

const UnavailableChip = React.forwardRef((props: ChipProps, ref) => (
  <ThemeableChip {...props} theme={Theme.unavailable} ref={ref} />
));
UnavailableChip.displayName = 'UnavailableChip';
UnavailableChip.defaultProps = { 'data-provides': 'unavailable-chip' };

export { ErrorChip, UnavailableChip, Chip };
